import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, Radio, Search } from "lucide-react";
import Modal from "../../../components/Modal";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import {
  controleProducaoService,
  CronometragemItem,
  FichaDiariaItem,
  FichaDiariaResponse,
  ProdutorOnlineSetor,
  TurnoProducao,
  UndMedidaOption,
} from "../../../services/controleProducaoService";
import { formatQuantidadeComUnd, itemNaoUsaTimerNaFicha } from "../../../utils/controleProducaoQuantidade";
import { agruparItensFichaPorSetor } from "./agruparItensFichaPorSetor";
import type { EquipeSetorAlvo } from "./FichaDiariaEquipeSetorModal";
import {
  cronometragemComTick,
  formatTempoMs,
} from "./CronometroItemButtons";

type FichaDiariaVisaoCompletaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dataFicha: string;
  lojaId?: number;
  turnoSimulado?: TurnoProducao;
  onAbrirEquipeSetor?: (setor: EquipeSetorAlvo) => void;
};

function normalizarBuscaItem(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function chaveSetor(setorId: number | null): string {
  return setorId == null ? "sem" : String(setorId);
}

function Badge({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ${className}`}
    >
      {children}
    </span>
  );
}

function statusProducaoBadge(item: FichaDiariaItem) {
  if (item.somenteSobraOutraUnidade) {
    return { label: "Sobra", className: "bg-amber-100 text-amber-900" };
  }
  if (item.naoProduzidoPelaUnidade) {
    return { label: "Não produziu", className: "bg-slate-200 text-slate-800" };
  }
  if (item.producaoChecada) {
    return { label: "Produziu", className: "bg-emerald-100 text-emerald-800" };
  }
  return { label: "Pendente", className: "bg-neutral-200 text-neutral-600" };
}

function statusCronometroBadge(status?: string | null) {
  if (status === "RUNNING") {
    return { label: "Produzindo", className: "bg-sky-100 text-sky-800" };
  }
  if (status === "PAUSED") {
    return { label: "Pausado", className: "bg-amber-100 text-amber-900" };
  }
  if (status === "FINISHED") {
    return { label: "Encerrado", className: "bg-emerald-100 text-emerald-800" };
  }
  return null;
}

function producaoComUnd(
  item: FichaDiariaItem,
  unds: UndMedidaOption[]
): string {
  if (item.somenteSobraOutraUnidade || item.naoProduzidoPelaUnidade) return "—";
  const undId = item.undMedidaIdProducao ?? item.undMedidaIdBase;
  const persistida = formatQuantidadeComUnd(item.producao, undId, unds);
  if (persistida) return persistida;
  const sugestaoUnd = item.sugestaoUndMedidaIdProducao ?? undId;
  return formatQuantidadeComUnd(item.sugestaoProducao, sugestaoUnd, unds) || "—";
}

function sobraComUnd(
  item: FichaDiariaItem,
  unds: UndMedidaOption[]
): string {
  const undId =
    item.undMedidaIdSobra ??
    item.undMedidaIdSobraReferencia ??
    item.undMedidaIdBase;
  const persistida = formatQuantidadeComUnd(item.sobra, undId, unds);
  if (persistida) return persistida;
  const exibicao = (item.sobraExibicao ?? "").trim();
  if (exibicao) return exibicao;
  return "—";
}

function tempoOuTraco(valor?: string | null, ms?: number | null): string {
  const txt = (valor ?? "").trim();
  if (txt) return txt;
  if (ms != null && ms > 0) return formatTempoMs(ms);
  if (ms === 0) return "00:00";
  return "—";
}

function StatusBadges({
  item,
  crono,
  dataFicha,
}: {
  item: FichaDiariaItem;
  crono: CronometragemItem | null;
  dataFicha?: string | null;
}) {
  const producao = statusProducaoBadge(item);
  const cronometro = statusCronometroBadge(crono?.status);
  const semTimer =
    item.somenteSobraOutraUnidade === true ||
    itemNaoUsaTimerNaFicha(item.naoUsaTimer, dataFicha);
  return (
    <div className="flex flex-wrap gap-1">
      <Badge className={producao.className}>{producao.label}</Badge>
      {semTimer ? null : cronometro ? (
        <Badge className={cronometro.className}>{cronometro.label}</Badge>
      ) : null}
    </div>
  );
}

function UsuarioDoItem({ crono }: { crono: CronometragemItem | null }) {
  const nome = crono?.userNome?.trim();
  if (!nome) {
    return <span className="text-neutral-400">—</span>;
  }
  const fazendo = crono?.status === "RUNNING";
  const pausado = crono?.status === "PAUSED";
  const encerrado = crono?.status === "FINISHED";
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
      {fazendo || pausado ? (
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${
            pausado ? "bg-amber-500" : "bg-emerald-500"
          }`}
          aria-hidden
        />
      ) : null}
      <span className="min-w-0 truncate font-medium text-neutral-800">
        {nome}
      </span>
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
        {fazendo ? "Fazendo" : pausado ? "Pausado" : encerrado ? "Fez" : ""}
      </span>
    </span>
  );
}

function ProdutoresHeader({
  produtores,
}: {
  produtores: ProdutorOnlineSetor[];
}) {
  if (produtores.length === 0) return null;
  return (
    <ul className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
      {produtores.map((produtor) => {
        const pausado = produtor.status === "PAUSED";
        return (
          <li
            key={produtor.userId}
            className="inline-flex max-w-[10rem] items-center gap-1"
          >
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                pausado ? "bg-amber-500" : "bg-emerald-500"
              }`}
              aria-hidden
            />
            <span className="truncate text-[11px] font-medium text-neutral-700">
              {produtor.nome}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function itensProgressoDoSetor(itens: FichaDiariaItem[]): FichaDiariaItem[] {
  return itens.filter((item) => (item.indexNoGrupo ?? 0) === 0);
}

function chaveLinhaFicha(item: FichaDiariaItem, sufixo: string): string {
  return `${item.itemId}-${item.lojaIdOutra ?? "local"}-${sufixo}`;
}

function labelUnidadeFicha(item: FichaDiariaItem): string {
  return (item.unidadeLabel ?? "").replace("Dom Belloni - ", "");
}

function itemProducaoResolvido(item: FichaDiariaItem): boolean {
  return item.producaoChecada === true || item.naoProduzidoPelaUnidade === true;
}

function progressoDoSetor(itens: FichaDiariaItem[]): {
  feitos: number;
  total: number;
  pct: number;
} {
  const visiveis = itensProgressoDoSetor(itens);
  const total = visiveis.length;
  const feitos = visiveis.filter(itemProducaoResolvido).length;
  const pct = total === 0 ? 0 : Math.round((feitos / total) * 100);
  return { feitos, total, pct };
}

function BarraProgressoSetor({
  feitos,
  total,
  pct,
}: {
  feitos: number;
  total: number;
  pct: number;
}) {
  const completo = pct >= 100 && total > 0;
  return (
    <div className="mt-1.5 min-w-[8rem] flex-1">
      <div
        className="h-1.5 overflow-hidden rounded-full bg-white/80 ring-1 ring-primary/10"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`${pct}% concluído`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${
            completo ? "bg-emerald-500" : "bg-primary"
          }`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <p className="mt-1 text-[10px] font-semibold tabular-nums text-neutral-500">
        {feitos}/{total} · {pct}%
      </p>
    </div>
  );
}

function ListaItensSetor({
  itens,
  unds,
  nowMs,
  dataFicha,
}: {
  itens: FichaDiariaItem[];
  unds: UndMedidaOption[];
  nowMs: number;
  dataFicha?: string | null;
}) {
  return (
    <>
      <div className="divide-y divide-neutral-100 sm:hidden">
        {itens.map((item) => {
          const crono = cronometragemComTick(item.cronometragem, nowMs);
          const semTimer =
            item.somenteSobraOutraUnidade === true ||
            itemNaoUsaTimerNaFicha(item.naoUsaTimer, dataFicha);
          return (
            <article key={chaveLinhaFicha(item, "m")} className="px-3 py-2.5">
              <p className="text-sm font-semibold leading-snug text-neutral-900">
                {item.descricao}
                {item.unidadeLabel ? (
                  <span
                    className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      item.lojaIdOutra
                        ? "bg-sky-100 text-sky-800"
                        : "bg-emerald-100 text-emerald-800"
                    }`}
                  >
                    {labelUnidadeFicha(item)}
                  </span>
                ) : null}
              </p>
              <div className="mt-1.5">
                <StatusBadges item={item} crono={crono} dataFicha={dataFicha} />
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-neutral-600">
                <div>
                  <dt className="font-medium text-neutral-400">Base</dt>
                  <dd>{item.base || "—"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-400">Sobra</dt>
                  <dd>{sobraComUnd(item, unds)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-400">Produção</dt>
                  <dd>{producaoComUnd(item, unds)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-400">
                    Tempo produção
                  </dt>
                  <dd className="font-mono tabular-nums">
                    {semTimer
                      ? "—"
                      : tempoOuTraco(
                          crono?.tempoFormatado,
                          crono?.tempoLiquidoMs
                        )}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-400">Tempo pausa</dt>
                  <dd className="font-mono tabular-nums">
                    {semTimer
                      ? "—"
                      : tempoOuTraco(
                          crono?.tempoPausaFormatado,
                          crono?.tempoPausaManualMs
                        )}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="font-medium text-neutral-400">Quem</dt>
                  <dd>
                    <UsuarioDoItem crono={crono} />
                  </dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Base</th>
              <th className="px-3 py-2">Sobra</th>
              <th className="px-3 py-2">Produção</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Quem</th>
              <th className="px-3 py-2">Tempo produção</th>
              <th className="px-3 py-2">Tempo pausa</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const crono = cronometragemComTick(
                item.cronometragem,
                nowMs
              );
              const semTimer =
                item.somenteSobraOutraUnidade === true ||
                itemNaoUsaTimerNaFicha(
                item.naoUsaTimer,
                dataFicha
              );
              return (
                <tr
                  key={chaveLinhaFicha(item, "d")}
                  className="border-t border-neutral-100"
                >
                  <td className="px-3 py-2 font-medium leading-snug text-neutral-900">
                    {item.descricao}
                    {item.unidadeLabel ? (
                      <span
                        className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          item.lojaIdOutra
                            ? "bg-sky-100 text-sky-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {labelUnidadeFicha(item)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {item.base || "—"}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {sobraComUnd(item, unds)}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {producaoComUnd(item, unds)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadges
                      item={item}
                      crono={crono}
                      dataFicha={dataFicha}
                    />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <UsuarioDoItem crono={crono} />
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums text-neutral-800">
                    {semTimer
                      ? "—"
                      : tempoOuTraco(
                          crono?.tempoFormatado,
                          crono?.tempoLiquidoMs
                        )}
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums text-neutral-800">
                    {semTimer
                      ? "—"
                      : tempoOuTraco(
                          crono?.tempoPausaFormatado,
                          crono?.tempoPausaManualMs
                        )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

const FichaDiariaVisaoCompletaModal: React.FC<
  FichaDiariaVisaoCompletaModalProps
> = ({
  isOpen,
  onClose,
  dataFicha,
  lojaId,
  turnoSimulado,
}) => {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [payload, setPayload] = useState<FichaDiariaResponse | null>(null);
  const [aoVivo, setAoVivo] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [buscaProduto, setBuscaProduto] = useState("");
  const [abertos, setAbertos] = useState<Set<string>>(() => new Set());
  const iniciouAbertosRef = useRef(false);

  const carregar = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
        setErro(null);
      }
      try {
        const data = await controleProducaoService.getFichaDiaria(
          dataFicha,
          lojaId,
          undefined,
          turnoSimulado,
          true
        );
        setPayload(data);
        setNowTick(Date.now());
      } catch (error: { message?: string } | unknown) {
        const message =
          error && typeof error === "object" && "message" in error
            ? String((error as { message?: string }).message)
            : "Não foi possível carregar a ficha completa.";
        if (!silent) setErro(message);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dataFicha, lojaId, turnoSimulado]
  );

  useEffect(() => {
    if (!isOpen) {
      setPayload(null);
      setErro(null);
      setAoVivo(false);
      setBuscaProduto("");
      setAbertos(new Set());
      iniciouAbertosRef.current = false;
      return;
    }
    void carregar();
  }, [isOpen, carregar]);

  useRankingEvents("ficha-diaria", lojaId, {
    queryKeys: [],
    enabled: isOpen,
    onAvailabilityChange: setAoVivo,
    onEvent: () => {
      void carregar({ silent: true });
    },
  });

  const unds = payload?.unds ?? [];
  const blocos = useMemo(() => {
    if (!payload?.itens?.length) return [];
    return agruparItensFichaPorSetor(
      payload.itens,
      payload.resumoSetores,
      unds
    );
  }, [payload, unds]);

  useEffect(() => {
    if (iniciouAbertosRef.current || blocos.length === 0) return;
    iniciouAbertosRef.current = true;
    setAbertos(new Set([chaveSetor(blocos[0].setorId)]));
  }, [blocos]);

  const termoBusca = normalizarBuscaItem(buscaProduto);
  const buscando = termoBusca.length > 0;

  const blocosExibidos = useMemo(() => {
    return blocos
      .map((bloco) => {
        const visiveis = bloco.itens;
        const itens = buscando
          ? visiveis.filter((item) =>
              normalizarBuscaItem(item.descricao).includes(termoBusca)
            )
          : visiveis;
        return { bloco, itens };
      })
      .filter((entrada) => !buscando || entrada.itens.length > 0);
  }, [blocos, buscando, termoBusca]);

  const blocosRef = useRef(blocos);
  blocosRef.current = blocos;

  useEffect(() => {
    if (!buscando) return;
    const next = new Set<string>();
    for (const bloco of blocosRef.current) {
      const bate = bloco.itens.some((item) =>
        normalizarBuscaItem(item.descricao).includes(termoBusca)
      );
      if (bate) next.add(chaveSetor(bloco.setorId));
    }
    setAbertos(next);
  }, [buscando, termoBusca]);

  const temCronometroAndando = useMemo(
    () =>
      blocos.some((bloco) =>
        bloco.itens.some((item) => {
          const status = item.cronometragem?.status;
          return status === "RUNNING" || status === "PAUSED";
        })
      ),
    [blocos]
  );

  useEffect(() => {
    if (!isOpen || !temCronometroAndando) return;
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen, temCronometroAndando]);

  useEffect(() => {
    if (!isOpen) return;
    const bumpTick = () => setNowTick(Date.now());
    const onVisibility = () => {
      if (document.visibilityState === "visible") bumpTick();
    };
    window.addEventListener("pageshow", bumpTick);
    window.addEventListener("focus", bumpTick);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", bumpTick);
      window.removeEventListener("focus", bumpTick);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isOpen]);

  const alternarSetor = (chave: string) => {
    setAbertos((atual) => {
      const next = new Set(atual);
      if (next.has(chave)) next.delete(chave);
      else next.add(chave);
      return next;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ficha de produção completa"
      description="Consulta em tempo real de todos os setores. Os lançamentos continuam sendo feitos em cada setor."
      panelClassName="max-w-6xl"
      bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5"
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              aoVivo
                ? "bg-emerald-100 text-emerald-800"
                : "bg-neutral-100 text-neutral-600"
            }`}
          >
            <Radio className="h-3 w-3" aria-hidden />
            {aoVivo ? "Ao vivo" : "Atualizando…"}
          </span>
          <button
            type="button"
            onClick={() => void carregar()}
            className="inline-flex h-8 items-center rounded-lg border border-neutral-300 bg-white px-2.5 text-[11px] font-semibold text-neutral-700 touch-manipulation hover:bg-neutral-50"
          >
            Atualizar
          </button>
        </div>
        <label className="min-w-0 flex-1">
          <span className="sr-only">Buscar produto</span>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
              aria-hidden
            />
            <input
              type="search"
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(e.target.value)}
              placeholder="Buscar produto…"
              className="h-10 w-full rounded-xl border border-neutral-300 py-2 pl-9 pr-3 text-sm"
              autoComplete="off"
            />
          </div>
        </label>
      </div>

      {loading ? (
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-neutral-600">
            Carregando ficha completa…
          </p>
        </div>
      ) : erro ? (
        <p className="py-10 text-center text-sm text-red-600">{erro}</p>
      ) : blocos.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Nenhum item para exibir nesta ficha.
        </p>
      ) : blocosExibidos.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Nenhum produto encontrado para “{buscaProduto.trim()}”.
        </p>
      ) : (
        <div className="space-y-3">
          {blocosExibidos.map(({ bloco, itens }) => {
            const progresso = progressoDoSetor(bloco.itens);
            const chave = chaveSetor(bloco.setorId);
            const aberto = abertos.has(chave);
            const produtores =
              payload?.resumoSetores?.find((s) => s.setorId === bloco.setorId)
                ?.produtoresOnline ?? [];
            const painelId = `setor-completo-${chave}`;
            return (
              <section
                key={`completo-${chave}`}
                className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
              >
                <h3 className="m-0">
                  <button
                    type="button"
                    aria-expanded={aberto}
                    aria-controls={painelId}
                    onClick={() => alternarSetor(chave)}
                    className="flex w-full flex-col bg-[#fff5f5] px-3 py-2 text-left touch-manipulation"
                  >
                    <span className="flex items-center gap-2">
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-primary transition-transform ${
                          aberto ? "rotate-0" : "-rotate-90"
                        }`}
                        aria-hidden
                      />
                      <span className="min-w-0 truncate text-xs font-bold uppercase tracking-wide text-primary">
                        {bloco.setorNome || "Sem setor"}
                      </span>
                      <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold tabular-nums tracking-normal text-primary ring-1 ring-primary/15">
                        {progresso.pct}%
                      </span>
                      {buscando ? (
                        <span className="shrink-0 text-[10px] font-semibold text-neutral-500">
                          {itens.length}{" "}
                          {itens.length === 1 ? "item" : "itens"}
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1">
                        <ProdutoresHeader produtores={produtores} />
                      </span>
                    </span>
                    <BarraProgressoSetor {...progresso} />
                  </button>
                </h3>
                {aberto ? (
                  <div id={painelId}>
                    <ListaItensSetor
                      itens={itens}
                      unds={unds}
                      nowMs={nowTick}
                      dataFicha={dataFicha}
                    />
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default FichaDiariaVisaoCompletaModal;
